import React, { useEffect, useState } from 'react';
import {
  useSchedulingContext,
  useSchedulingUpdateContext,
} from '../../../context';
import {
  AppointmentInfo,
  AppointmentType,
  AppointmentTypeQuestion,
  ProviderDetails,
  findAppointmentTypeInList,
} from '../../../../api/models';
import AppointmentInfoStepPresenter from './AppointmentInfoStepPresenter';
import {
  GetAcceptFormatQuestionsApiService,
  GetAcceptFormatQuestionsRequest,
} from '../../../../api/services';
import { PresenterState } from './PresenterState';
import {
  GetAppointmentTypeReturnValue,
  updateProviderAppointmentTypes,
} from '../../../../utils';
import { EnvironmentExtended } from '../../../../environment/index';

export interface AppointmentInfoStepWrapperProps {
  onContinue: () => void;
}

const AppointmentInfoStepWrapper: React.FC<AppointmentInfoStepWrapperProps> = (
  props: AppointmentInfoStepWrapperProps
) => {
  const [presenterState, setPresenterState] = useState<PresenterState>(
    PresenterState.Ready
  );
  const schedulingDetails = useSchedulingContext();
  const schedulingUpdateContext = useSchedulingUpdateContext();

  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (loaded && schedulingDetails.appointmentInfo) {
      const appointmentType = findAppointmentTypeInList(
        schedulingDetails.providerDetails?.appointmentTypeList,
        schedulingDetails.appointmentInfo.appointmentTypeId
      );

      if (appointmentType?.questions !== undefined) {
        props.onContinue();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulingDetails.appointmentInfo]);

  useEffect(() => {
    const getAppointmentTypes = async () => {
      if (schedulingDetails.providerDetails?.practitionerId) {
        setPresenterState(PresenterState.LoadingAppointmentTypes);
        const result = await updateProviderAppointmentTypes(
          schedulingDetails,
          schedulingUpdateContext
        );
        if (result === GetAppointmentTypeReturnValue.Success) {
          setPresenterState(PresenterState.Ready);
        } else if (result === GetAppointmentTypeReturnValue.Empty) {
          setPresenterState(PresenterState.NoAppointmentTypes);
        } else {
          setPresenterState(PresenterState.Error);
        }
      } else {
        setPresenterState(PresenterState.Error);
      }
    };

    if (
      schedulingDetails.providerDetails?.appointmentTypeList === undefined ||
      schedulingDetails.providerDetails?.appointmentTypeList.length === 0
    ) {
      getAppointmentTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulingDetails.providerDetails?.practitionerId]);

  const onAppointmentTypeSelected = (appointmentTypeId: string) => {
    const updateApptTypeQuestions = (
      questionList: AppointmentTypeQuestion[]
    ): boolean => {
      let appointmentTypeList =
        schedulingDetails?.providerDetails?.appointmentTypeList;
      const appointmentType = findAppointmentTypeInList(
        appointmentTypeList,
        appointmentTypeId
      );

      if (appointmentType === undefined) {
        return false;
      }

      const newAppointmentType = {
        ...appointmentType,
        questions: questionList,
      };

      appointmentTypeList = appointmentTypeList?.map((oldApptType) =>
        oldApptType.id === appointmentTypeId
          ? (newAppointmentType as AppointmentType)
          : oldApptType
      );

      schedulingUpdateContext.changeSchedulingDetails({
        ...schedulingDetails,
        providerDetails: {
          ...(schedulingDetails.providerDetails as ProviderDetails),
          appointmentTypeList: appointmentTypeList,
        },
      });

      return true;
    };

    const getQuestions = async () => {
      if (
        schedulingDetails.providerDetails?.practitionerId &&
        schedulingDetails.providerDetails?.appointmentTypeList &&
        schedulingDetails.providerDetails?.appointmentTypeList.length > 0 &&
        appointmentTypeId.length > 0
      ) {
        setPresenterState(PresenterState.LoadingQuestions);

        const request: GetAcceptFormatQuestionsRequest = {
          appointmentTypeId: appointmentTypeId,
        };

        const response = await GetAcceptFormatQuestionsApiService.execute(
          request
        );
        if (response.succeeded && response.data) {
          if (
            response.data.acceptFormatQuestionList &&
            response.data.acceptFormatQuestionList.length > 0
          ) {
            const questionList = response.data.acceptFormatQuestionList;

            if (!updateApptTypeQuestions(questionList)) {
              setPresenterState(PresenterState.Error);
              return;
            }
          } else {
            updateApptTypeQuestions([]);
          }
          setPresenterState(PresenterState.Ready);
        } else {
          setPresenterState(PresenterState.Error);
        }
      } else {
        setPresenterState(PresenterState.Error);
      }
    };

    const appointmentType = findAppointmentTypeInList(
      schedulingDetails.providerDetails?.appointmentTypeList,
      appointmentTypeId
    );

    if (appointmentType?.questions === undefined) {
      getQuestions();
    }
  };

  const onContinue = (appointmentInfo: AppointmentInfo) => {
    schedulingUpdateContext.changeSchedulingDetails({
      ...schedulingDetails,
      appointmentInfo: { ...appointmentInfo },
    });
  };

  const onRedirectToProviderDirectory = () => {
    window.open(`${EnvironmentExtended.getProviderDirectoryUrl()}`, '_self');
  };

  return (
    <AppointmentInfoStepPresenter
      schedulingDetails={schedulingDetails}
      state={presenterState}
      onAppointmentTypeSelected={onAppointmentTypeSelected}
      onContinue={onContinue}
      onNoAppointmentTypesRedirect={onRedirectToProviderDirectory}
    />
  );
};

export default AppointmentInfoStepWrapper;
